// Shared formatting for scheduled_trigger_runs — friendly status text + a
// relative time that tolerates the server's SQL timestamp format
// ("2026-08-20 06:19:38", space-separated, no zone).

export function runStatusLabel(status: string): string {
  switch (status) {
    case 'spawned_temp':
    case 'woke_existing':
    case 'delivered_live':
      return 'Ran'
    case 'dispatched':
      return 'Running…'
    // The daemon has the kickoff and is holding it until the agent finishes
    // what it's doing. Distinct from 'dispatched' (we're waiting on the host)
    // and from 'Ran' (the transcript confirmed the agent received it).
    case 'queued':
      return 'Waiting for the agent'
    case 'skipped_host_offline':
      return 'Skipped — host offline'
    case 'skipped_overlap':
      return 'Skipped — still running'
    case 'skipped_missed':
      return 'Skipped — missed'
    case 'failed':
      return 'Failed'
    default:
      return status
  }
}

// Rough coloring bucket for the UI: ok | warn | error | neutral.
export function runStatusTone(status: string): 'ok' | 'warn' | 'error' | 'neutral' {
  if (status === 'spawned_temp' || status === 'woke_existing' || status === 'delivered_live') return 'ok'
  if (status === 'failed') return 'error'
  if (status.startsWith('skipped')) return 'warn'
  // 'dispatched' and 'queued' are in-flight, not outcomes — neutral, like the
  // default. Named here so the two non-terminal statuses stay visible.
  return 'neutral'
}

// parseServerTime handles both shapes the relay sends: RFC3339 on some fields,
// and SQL's native "2026-08-20 06:19:38" (UTC, space-separated, no zone) on
// anything read straight out of a DATETIME column.
//
// The second shape is the trap: `new Date("2026-08-20 06:19:38")` parses as
// LOCAL time in every engine, so a UTC column silently shifts by the viewer's
// offset. Anything reading a timestamp off a trigger row must come through
// here. Returns NaN for unparseable input; callers check.
export function parseServerTime(value: string): number {
  if (!value) return NaN
  return Date.parse(value.includes('T') ? value : value.replace(' ', 'T') + 'Z')
}

export function runRelativeTime(iso: string): string {
  const t = parseServerTime(iso)
  if (Number.isNaN(t)) return ''
  const diff = Date.now() - t
  const mins = Math.round(diff / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 90) return `${mins}m ago`
  const hrs = Math.round(diff / 3_600_000)
  if (hrs < 48) return `${hrs}h ago`
  return `${Math.round(diff / 86_400_000)}d ago`
}
